import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { DataExchangeService } from '../../../services/session/data-exchange.service';
import { catchError, EMPTY } from 'rxjs';
import { FormGroup, FormControl, Validators, ValidatorFn, FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { FormFieldBase } from '../../../classes/form-field-base';
import { FormFieldTextbox } from '../../../classes/form-field-textbox'
import { SupportService } from '../../../services/support.service';
import { EnrollmentVerificationService } from '../../../services/enrollment/enrollment-verification.service';
import { DynamicFormFieldComponent } from '../../section-blocks/dynamic-form-field/dynamic-form-field.component';
import { ISessionDetails } from '../../../interfaces/session-details';
import { IEvFieldUsage } from '../../../interfaces/enrollment/ev-field-usage';
import { ExceptionEvent } from '../../../classes/exception-event';
import { NGXLogger } from 'ngx-logger';
import { CustomLoggerService } from '../../../services/custom-logger.service';

interface KeyValue {
  key: string;
  value: string;
}

@Component({
    selector: 'app-enrollment-verification',
    standalone: true,
    templateUrl: './enrollment-verification.component.html',
    styleUrls: ['./enrollment-verification.component.css'],
    providers: [],
    imports: [
        ReactiveFormsModule,
        DynamicFormFieldComponent,
    ]
})
export class EnrollmentVerificationComponent implements OnInit {

    private readonly router = inject(Router);
    private readonly dataExchangeService = inject(DataExchangeService);
    private readonly supportService = inject(SupportService);
    private readonly enrollmentVerificationService = inject(EnrollmentVerificationService);
    private readonly logger = inject(NGXLogger);
    private readonly customLogger = inject(CustomLoggerService);
    private readonly sanitizer = inject(DomSanitizer);

  evFieldUsages: IEvFieldUsage[] = [];
  formFields: FormFieldBase<string>[] | null = [];
  form!: FormGroup;

  sessionId!: string;
  brandCode!: string;
  region!: string;

  evProps!: Map<string,string>;
  overrideProps!: KeyValue[];

  successMessage!: SafeHtml;
  errorMessage!: SafeHtml;
  isSuccessMessageShown: boolean = false;
  isErrorMessageShown: boolean = false;

  headerText!: SafeHtml | undefined;
  subheaderText!: SafeHtml | undefined;  
  promptText!: SafeHtml | undefined;
  consentPrompt!: SafeHtml | undefined;
  moreInfo!: SafeHtml | undefined;
  footerText!: SafeHtml | undefined;
  btnSubmit!: string;
  btnCancel!: string;
  btnCancelUrl!: string;
  btnSubmitStyle!: string;
  btnCancelStyle!: string;

  isHeaderTextShown: boolean = false;
  isSubheaderTextShown: boolean = false;
  isPromptTextShown: boolean = false;
  isConsentPromptShown: boolean = false;
  isMoreInfoShown: boolean = false;
  isFooterTextShown: boolean = false;
  isEvFieldsShown: boolean = true;
  isCloseButtonShown: boolean = false;

  constructor(
    private readonly exceptionEvent: ExceptionEvent
    ) {
  }

  ngOnInit() {

    // console.log('Logger level:', this.logger.getConfigSnapshot().level); // ✅ Should be 0 (DEBUG)
//     this.logger.trace('Trace log');
//     this.logger.debug('Debug log');
//     this.logger.info('Info log');
//      this.logger.log('Log log');
//     this.logger.error('Warning log');
// console.warn('MY WARNING');
// console.error('MY ERROR');


    // this.exceptionEvent.initialize('ev-page', '25', 'test message 25');
    // const exceptionJson = this.exceptionEvent.toJson();

    // this.customLogger.logError(exceptionJson);
        // this.customLogger.logException('ev-page.ngOnInit()', 'EV-0', 'Unable to determine session/region', 'ENROLLMENT');


    // Initialization of the form
    const fieldGroup: any = {};
    this.form = new FormGroup(fieldGroup);
    // Get the parameters from the caller (Walletron)
    this.region = this.dataExchangeService.getRegion();
    let session: ISessionDetails = this.dataExchangeService.getSession();

    if (this.region && session) {
      this.sessionId = session.walPageUuid;

      // Get EV config properties - Prepare the header, prompt, buttons, etc.
      this.getEvConfigProperties(this.region, this.sessionId);

      // Retrieve EV fields
      this.geEvFields(this.region, this.sessionId);
    } else {
        this.customLogger.logException('ev-page.ngOnInit()', 'EV-0', 'Unable to determine session/region', 'ENROLLMENT');
        // this.exceptionEvent.initialize('ev-page.ngOnInit()', 'EV-0', 'Unable to determine session/region', 'OTHER', 'ENROLLMENT', 'MODERATE');
        // const exceptionJson = this.exceptionEvent.toJson();
        // this.customLogger.logError(exceptionJson);
        // this.dataExchangeService.setMessageText('System error - see account administrator');
        // this.router.navigate(['/err']);
    }
  }

  // Prepare the header, prompt, buttons, etc.
  getEvConfigProperties(region: string, sessionId: string) {
    this.logger.log('ev-page - getEvConfigProperties before', new Date().toISOString());
    this.enrollmentVerificationService.getEvConfigProperties(region, sessionId)
    .subscribe({
      next: (response) => {

        this.evProps = response;
        this.logger.log('ev-page - getEvConfigProperties response', response);

        // Prepare the header, prompt, buttons, etc. — check raw strings before sanitizing.
        const rawHeaderText    = this.getPropertyValue("EAUTH_HEADER");
        const rawSubheaderText = this.getPropertyValue("EAUTH_SUBHEADER");
        const rawPromptText    = this.getPropertyValue("EAUTH_PROMPT");
        const rawConsentPrompt = this.getPropertyValue("EAUTH_CONSENT_PROMPT");
        const rawMoreInfo      = this.getPropertyValue("EAUTH_MORE_INFO");
        const rawFooterText    = this.getPropertyValue("EAUTH_FOOTER");

        this.btnSubmit    = this.getPropertyValue("EAUTH_SUBMIT_CMD", 'Submit');
        this.btnCancel    = this.getPropertyValue("EAUTH_CANCEL_CMD", 'Cancel');
        this.btnCancelUrl = this.getPropertyValue("EAUTH_CANCEL_CMD_URL", 'https://www.aciworldwide.com');

        // Can we show them or hide?
        this.isHeaderTextShown    = !!rawHeaderText    && rawHeaderText.trim()    !== "";
        this.isSubheaderTextShown = !!rawSubheaderText && rawSubheaderText.trim() !== "";
        this.isPromptTextShown    = !!rawPromptText    && rawPromptText.trim()    !== "";
        this.isConsentPromptShown = !!rawConsentPrompt && rawConsentPrompt.trim() !== "";
        this.isMoreInfoShown      = !!rawMoreInfo      && rawMoreInfo.trim()      !== "";
        this.isFooterTextShown    = !!rawFooterText    && rawFooterText.trim()    !== "";

        // Sanitize for [innerHTML] binding
        this.headerText    = this.sanitizer.bypassSecurityTrustHtml(rawHeaderText);
        this.subheaderText = this.sanitizer.bypassSecurityTrustHtml(rawSubheaderText);
        this.promptText    = this.sanitizer.bypassSecurityTrustHtml(rawPromptText);
        this.consentPrompt = this.sanitizer.bypassSecurityTrustHtml(rawConsentPrompt);
        this.moreInfo      = this.sanitizer.bypassSecurityTrustHtml(rawMoreInfo);
        this.footerText    = this.sanitizer.bypassSecurityTrustHtml(rawFooterText);
        this.logger.log('ev-page - getEvConfigProperties after', new Date().toISOString());

      },
      error: (error) => {
        this.customLogger.logException('ev-page.getEvConfigProperties()', 'EV-1', error.message, 'ENROLLMENT');
      },
      complete: () => {
      }
    });

  }

  // Find out the value of a given EV brand config property
  getPropertyValue(propName: string, defaultValue: string = ''): string {
    // // Prefer overrideProps if a matching key is found
    // const overrideValue = this.overrideProps?.find(({ key }) => key === propName)?.value;
    // if (overrideValue !== undefined) {
    //   return overrideValue;
    // }
    // Fallback to evProps or default
    return this.evProps.get(propName) ?? defaultValue;
  }

  // Retrieve a list of EV fields for the given EV session
  geEvFields(region: string, sessionId: string) {
    this.logger.log('ev-page - geEvFields before', new Date().toISOString());
    // Make a Web API call to retrieve a list of EV fields
    this.enrollmentVerificationService.getEvFields(region, sessionId)
    .subscribe({
      next: (response) => {
        if (!response) return;
        this.evFieldUsages = response;
          // console.log('this.evFieldUsages', this.evFieldUsages);

        // Build dynamically Enrrollment Verification form
        const fieldGroup: any = {};
        this.evFieldUsages.forEach(evFieldUsage => {
          const evField = evFieldUsage.evField;
          // console.log('evField', evField);
          // Add validation for required input
          let validator: ValidatorFn[] = [Validators.required];
          // Add specific pattern validation
          if (evField.pattern) {
              validator.push(Validators.pattern(evField.pattern));
          }
          // Dynamically construct different fields for the form
          switch (evField.inputType) {
            case 'tel-mask':
              // Construct a telephone mask field for the form
              this.formFields?.push(new FormFieldBase({
                key: evField.fieldName,
                label: evField.label,
                required: true,
                controlType: evField.inputType,
                placeholder: evField.placeholder!,
                pattern: evField.pattern!,
                invalidFormatMsg: evField.invalidFormatMsg!,
                styles: evField.styles!,
                value: evFieldUsage.fieldValue!
              }));
              break;
            default:
              // Construct a default "regular textbox" field for the form
              this.formFields?.push(new FormFieldTextbox({
                key: evField.fieldName,
                label: evField.label,
                inputType: evField.inputType || 'text',
                required: true,
                // order: 1,
                // controlType: evField.inputType || 'textbox',
                placeholder: evField.placeholder!,
                pattern: evField.pattern!,
                invalidFormatMsg: evField.invalidFormatMsg!,
                maxLength: evField.maxLength!,
                styles: evField.styles!,
                mask: evField.mask!,
                value: evFieldUsage.fieldValue!
              }));
              break;
          }
          fieldGroup[evField.fieldName] = validator.length > 0 ? new FormControl(null, validator)
                : new FormControl(null);
          fieldGroup[evField.fieldName].setValue(evFieldUsage.fieldValue);
        });
        this.form = new FormGroup(fieldGroup);
        if (this.formFields) {
          this.isEvFieldsShown = true;
        } else {
          this.isEvFieldsShown = false;
        }
        this.logger.debug('ev-page - geEvFields after', new Date().toISOString());
      },
      error: (error) => {
        this.customLogger.logException('ev-page.geEvFields()', 'EV-2', error.message, 'ENROLLMENT');
      },
      complete: () => {
      }
    });
  }

  onSubmitForEv(form: any) {
    this.logger.debug('onSubmit', form);
    const evMap = new Map<string, any>(
      Object.entries(form)
    );

    // Reformat the dates to ISO
    evMap.forEach ((value, key) => {
      // if (this.supportService.isValidDateString(value)) {
      const index = this.evFieldUsages
        .findIndex(evFieldUsage => evFieldUsage.evField.fieldName === key && evFieldUsage.evField.inputType === 'date');
      if (index >= 0) {
        evMap.set(key, this.supportService.formatDateToISO(String(value)));
      }
    })
    this.logger.debug('evMap', evMap);

    this.isSuccessMessageShown = false;
    this.isErrorMessageShown = false;
    this.enrollmentVerificationService.sendEvRequest(this.region, this.sessionId, evMap)
      .pipe(
        catchError((error) => {
          this.customLogger.logException('ev-page.onSubmitForEv()', 'EV-3', error.message, 'ENROLLMENT');
          this.errorMessage = this.sanitizer.bypassSecurityTrustHtml('An error occured.  Please call system administrator.');
          this.isErrorMessageShown = true;
          return EMPTY;
        })
      )
      .subscribe({
        next: (response) => {
          this.logger.debug('onSubmitForEv - response', response);

          switch (response.status) {
            case 'BAD_REQUEST':
              this.errorMessage = this.sanitizer.bypassSecurityTrustHtml(response.returnMessage || 'System error.  Please see system administrator.');
              this.isErrorMessageShown = true;
              this.isEvFieldsShown = false;
              break;
            case 'ERROR':
              this.errorMessage = this.sanitizer.bypassSecurityTrustHtml(response.returnMessage || 'Sorry, looks like some kind of a system error happened.  Please try again.');
              this.isErrorMessageShown = true;
              break;
            case 'FAILURE':
              if (response.additionalInfo?.has('evStatus')) {
                switch(response.additionalInfo.get('evStatus')) {
                  case 'EXPIRED':
                    this.errorMessage = this.sanitizer.bypassSecurityTrustHtml(this.getPropertyValue("EAUTH_EXPIRED_SESSION_MSG", 'Sorry... Your session has expired. Good bye!'));
                    this.isEvFieldsShown = false;
                    this.isPromptTextShown = false;
                    this.isCloseButtonShown = true;
                    break;
                  case 'EXCEEDED_ATTEMPTS_LIMIT':
                    this.errorMessage = this.sanitizer.bypassSecurityTrustHtml(this.getPropertyValue("EAUTH_EXCEEDED_ATTEMPTS_LIMIT_MSG", 'Sorry... we cannot find the match. Good bye!'));
                    this.isEvFieldsShown = false;
                    this.isPromptTextShown = false;
                    this.isCloseButtonShown = true;
                    break;
                  case 'ACCOUNT_NOT_FOUND':
                    this.errorMessage = this.sanitizer.bypassSecurityTrustHtml(this.getPropertyValue("EAUTH_ACCOUNT_NOT_FOUND_MSG", 'Account not found, please try again.'));
                    break;
                  case 'NO_MATCH_TRY_AGAIN':
                    this.errorMessage = this.sanitizer.bypassSecurityTrustHtml(this.getPropertyValue("EAUTH_NO_MATCH_TRY_AGAIN_MSG", 'Account not found, please try again.'));
                    break;
                  case 'ACCOUNT_INACTIVE':
                    this.errorMessage = this.sanitizer.bypassSecurityTrustHtml(this.getPropertyValue("EAUTH_ACCOUNT_INACTIVE_MSG", 'Sorry, your account is inactive. Good bye!'));
                    this.isEvFieldsShown = false;
                    this.isPromptTextShown = false;
                    this.isCloseButtonShown = true;
                    break;
                }
              }
              this.isErrorMessageShown = true;
              break;
            case 'SUCCESS':
              this.successMessage = this.sanitizer.bypassSecurityTrustHtml(response.returnMessage || '');
              const afterPrompt = this.getPropertyValue("EAUTH_AFTER_SUBMIT_TEXT");
              this.promptText = this.sanitizer.bypassSecurityTrustHtml(afterPrompt ? afterPrompt : 'Make sure that you tapped Add link (on iPhone) or pressed Save button (on Android phone)');
              this.isSuccessMessageShown = true;
              this.isEvFieldsShown = false;
              if (response.additionalInfo && response.additionalInfo.has('redirectUrl')) {
                let redirectUrl = response.additionalInfo.get('redirectUrl');
                this.logger.log('onSubmitForEv - redirectUrl', redirectUrl);
                if (redirectUrl) {
                  window.location.href = redirectUrl;
                }
              }
              break;
          }
        },
        error: (error) => {
          console.log('onSubmitForEv - error', error);
          console.log('onSubmitForEv - error.message', error.message);
          this.customLogger.logException('ev-page.onSubmitForEv()', 'EV-4', error.message, 'ENROLLMENT');
        },
      });

  }

  onCancel() {
    window.location.href = this.btnCancelUrl;
  }

  onClose() {
    window.open('','_parent','');
    window.close();
  }

// getValueByKey(json: string, searchKey: string): string | undefined {
//   const parsed: KeyValue[] = JSON.parse(json);
//   const found = parsed.find(item => item.key === searchKey);
//   return found?.value;
// }

  // getConfigOverrides(session: ISessionDetails, region: string) {
  //   // Find out whether WAL session has an option with offerId
  //   if (session && session.options) {
  //     const options: Map<string, any> = new Map(Object.entries(session.options));
  //     if (options) {
  //       if (options.has("offerId")) {
  //         const offerId = options.get('offerId');
  //         // Retrieve offer fields for offerId
  //         this.enrollmentVerificationService.getOfferFields(region, session.brandCode || '', offerId)
  //         .subscribe({
  //           next: (response) => {
  //             // Locate the field with the key __BRAND_PROPS_OVERRIDES__
  //             const oneField = response.find(offerField => offerField.fieldKey == '__BRAND_PROPS_OVERRIDES__');
  //             if (oneField && oneField.fieldValue) {
  //               // Parse the value into a map, then locate specific properties in it
  //               const configMap: Map<string, string> = JSON.parse(oneField.fieldValue);
  //               if (configMap.has('EAUTH_SUBMIT_CMD_STYLE')) {
  //                 this.btnSubmitStyle = configMap.get('EAUTH_SUBMIT_CMD_STYLE') || '';
  //               }
  //               if (configMap.has('EAUTH_CANCEL_CMD_STYLE')) {
  //                 this.btnCancelStyle = configMap.get('EAUTH_CANCEL_CMD_STYLE') || '';
  //               }
  //               if (configMap.has('EAUTH_MORE_INFO')) {
  //                 this.moreInfo = configMap.get('EAUTH_MORE_INFO') || '';
  //                 this.isMoreInfoShown = !!this.moreInfo && this.moreInfo.trim() !== "";
  //               }
  //               if (configMap.has('EAUTH_FOOTER')) {
  //                 this.footerText = configMap.get('EAUTH_FOOTER') || '';
  //                 this.isFooterTextShown = !!this.footerText && this.footerText.trim() !== "";
  //               }
  //             }
  //           },
  //           error: (error) => {
  //             console.log('error', error);
  //             this.dataExchangeService.setMessageText(error);
  //             this.router.navigate(['/err']);
  //           },
  //           complete: () => {
  //           }
  //         });
  //       }
  //     }
  //   }

  // }


}


  // geEvFields(region: string, sessionId: string, brandCode: string) {
  //   this.evFields =
  //       [
  //           {
  //               "fieldName": "account",
  //               "fieldDataType": "STRING",
  //               "label": "Account #",
  //               "placeholder": "10-digit account #",
  //               "pattern": '^[a-zA-Z]+$',
  //               "invalidFormatMsg": "Enter exactly 10 digits",
  //               "maxLength": 10
  //           },
  //           {
  //               "fieldName": "last4SSN",
  //               "fieldDataType": "LONG",
  //               "label": "Last 4 digits of SSN#",
  //               "placeholder": "9999",
  //               "pattern": '^\\d{4}$',
  //               "invalidFormatMsg": "Enter 4 last digits of your SSN#",
  //               "maxLength": 4
  //           },
  //           // {
  //           //     "fieldName": "zipCode",
  //           //     "fieldDataType": "LONG",
  //           //     "label": "Zip Code",
  //           //     "placeholder": "5-digit zip code",
  //           //     "pattern": '^\\d{5}$',
  //           //     "invalidFormatMsg": "Enter the Zip Code",
  //           //     "maxLength": 10
  //           // },
  //           // {
  //           //     "fieldName": "phone",
  //           //     "inputType": "tel",
  //           //     "fieldDataType": "LONG",
  //           //     "label": "Primary Phone #",
  //           //     "placeholder": "(999) 999-9999",
  //           //     "pattern": '^\\(\\d{3}\\) \\d{3}-\\d{4}$',
  //           //     "invalidFormatMsg": "Enter the Primary Phone #",
  //           //     // "maxLength": 10
  //           // },
  //           // {
  //           //     "fieldName": "phone-mask-control",
  //           //     "inputType": "tel-mask",
  //           //     "fieldDataType": "LONG",
  //           //     "label": "Primary Phone # (mask control)",
  //           //     "placeholder": "(999) 999-9999",
  //           //     "pattern": '^\\(\\d{3}\\) \\d{3}-\\d{4}$',
  //           //     "invalidFormatMsg": "Enter the Primary Phone #",
  //           //     "maxLength": 10
  //           // },
  //           // {
  //           //     "fieldName": "phone-mask",
  //           //     "inputType": "tel",
  //           //     "fieldDataType": "LONG",
  //           //     "label": "Primary Phone # (with mask)",
  //           //     "placeholder": "(999) 999-9999",
  //           //     "pattern": '^\\d{10}$',
  //           //     "invalidFormatMsg": "Enter the Primary Phone #",
  //           //     "mask": "(000) 000-0000"
  //           //     // "maxLength": 10
  //           // },
  //           // {
  //           //     "fieldName": "state",
  //           //     "fieldDataType": "STRING",
  //           //     "label": "State",
  //           //     "value": "PA",
  //           //     "placeholder": "2-letter state code",
  //           //     "pattern": '^(A[LKZR]|C[AOT]|D[EC]|FL|GA|HI|I[ADLN]|K[SY]|LA|M[ADEINOST]|N[CDEHJMVY]|O[HKR]|PA|RI|S[CD]|T[NX]|UT|V[AIT]|W[AIVY])$',
  //           //     "invalidFormatMsg": "Enter the state code",
  //           //     "maxLength": 2
  //           // },
  //           // {
  //           //     "fieldName": "dateOfBirth",
  //           //     "fieldDataType": "STRING",
  //           //     "label": "Date of Birth",
  //           //     "value": "07/28/2025",
  //           //     "placeholder": "MM/DD/YYYY",
  //           //     "invalidFormatMsg": "Enter your date of birth as MM/DD/YYYY",
  //           //     "maxLength": 10
  //           // },
  //           // {
  //           //     "fieldName": "dateOfBirth-calendar",
  //           //     "value": "1999-01-03",
  //           //     "inputType": "date",
  //           //     "fieldDataType": "STRING",
  //           //     "label": "Date of Birth (Calendar)",
  //           //     "placeholder": "MM/DD/YYYY",
  //           //     "invalidFormatMsg": "Enter your date of birth as MM/DD/YYYY",
  //           //     "maxLength": 10
  //           // },
  //           // {
  //           //     "fieldName": "emailAddress",
  //           //     "inputType": "email",
  //           //     "fieldDataType": "STRING",
  //           //     "label": "Email Address",
  //           //     "pattern": "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$",
  //           //     "invalidFormatMsg": "Enter a valid email address",
  //           //     "maxLength": 100,
  //           //     "styles": {'width': '33em'}
  //           // }
  //       ];
  //   // Build dynamically Enrrollment Verification form
  //   const fieldGroup: any = {};
  //   this.evFields.forEach(evField => {
  //     // Add validation for required input
  //     let validator: ValidatorFn[] = [Validators.required];
  //     // Add specific pattern validation
  //     if (evField.pattern) {
  //         validator.push(Validators.pattern(evField.pattern));
  //     }
  //     // Dynamically construct different fields for the form
  //     switch (evField.inputType) {
  //       case 'tel-mask':
  //         // Construct a telephone mask field for the form
  //         this.formFields?.push(new FormFieldBase({
  //           key: evField.fieldName,
  //           label: evField.label,
  //           required: true,
  //           controlType: evField.inputType,
  //           placeholder: evField.placeholder!,
  //           pattern: evField.pattern!,
  //           invalidFormatMsg: evField.invalidFormatMsg!,
  //           styles: evField.styles!,
  //           value: evField.value!
  //         }));
  //         break;
  //       default:
  //         // Construct a default "regular textbox" field for the form
  //         this.formFields?.push(new FormFieldTextbox({
  //           key: evField.fieldName,
  //           label: evField.label,
  //           inputType: evField.inputType || 'text',
  //           required: true,
  //           // order: 1,
  //           // controlType: evField.inputType || 'textbox',
  //           placeholder: evField.placeholder!,
  //           pattern: evField.pattern!,
  //           invalidFormatMsg: evField.invalidFormatMsg!,
  //           maxLength: evField.maxLength!,
  //           styles: evField.styles!,
  //           mask: evField.mask!,
  //           value: evField.value!
  //         }));
  //         break;
  //     }
  //     fieldGroup[evField.fieldName] = validator.length > 0 ? new FormControl(null, validator)
  //           : new FormControl(null);
  //     fieldGroup[evField.fieldName].setValue(evField.value);
  //   });
  //   this.form = new FormGroup(fieldGroup);
  // }
